/**
 * One node, tested on its own.
 *
 * The site-wide diagnostics panel asks every node at once: Read Signal walks the
 * whole list and comes back "4/6 node(s) reported RSSI", which is the wrong
 * shape of answer for the question somebody standing under one node is actually
 * asking — is THIS one answering, and how well. Scoping the same two reads to a
 * single address gives a per-node answer without a new bridge command:
 * READ_SIGNAL and READ_STATES both already accept `router_macs`, and the bridge
 * has always honoured it (`node_macs()` in xbee_control.py).
 *
 * What lands where, and why the verdict needs two sources:
 *
 *   * the ACK (`DiagnosticResult.detail`) comes back with the command itself and
 *     carries the bridge's count — "1/1 node(s) reported RSSI". No value in it.
 *   * the READING goes up the bridge's outbox, and the outbox is drained at the
 *     START of a poll tick while commands run at the END of one. So the number
 *     is always a tick (~15 s) behind its own ack.
 *
 * The ack therefore decides whether the node answered; the refreshed node row
 * supplies the figure. Neither is enough alone: the ack has no dBm in it, and a
 * node row that has not moved yet is not evidence of silence.
 */

import type { DiagnosticResult } from '../services/espnowIrrigation';

/** What one of the two reads did. */
export type StepVerdict =
  /** The node replied. */
  | 'answered'
  /** The bridge asked and got nothing back — the actionable failure. */
  | 'silent'
  /** The bridge could not even ask: radio unplugged, command timed out. */
  | 'refused'
  /** Nothing came back, or came back in a shape this cannot read. */
  | 'no-result'
  /** Still outstanding. */
  | 'pending';

export interface TestStep {
  key: 'signal' | 'outputs';
  label: string;
  /** The bridge's own words, verbatim. Very often the whole diagnosis. */
  detail: string | null;
  verdict: StepVerdict;
}

/**
 * The leading count in a bridge ack — "1/1 node(s) reported RSSI", "0/1 node(s)
 * reported state". Returns how many of the nodes asked actually answered, or
 * null when the ack is not that shape.
 *
 * Reading prose is not free, and the wording could change on the bridge without
 * anything here failing loudly — hence the null fallback to node-row evidence
 * rather than treating an unreadable ack as a pass. But the count is the only
 * place a per-node outcome exists today: every one of these actions acks
 * CONFIRMED whether or not the node replied, because "asked, heard nothing" is
 * a successful run of the command from the bridge's point of view. Reading the
 * status alone would report a dead node as a clean test.
 */
export function answeredFromDetail(detail: string | null | undefined): number | null {
  if (!detail) return null;
  const match = /(\d+)\s*\/\s*(\d+)\s+node/i.exec(detail);
  if (!match) return null;
  const answered = Number(match[1]);
  return Number.isFinite(answered) ? answered : null;
}

/**
 * Signal bands, mirroring `signal_quality()` in the bridge's xbee_diagnostics.py
 * so one reading is never described two ways in two places. If those thresholds
 * move, they move here too.
 */
export function signalQuality(dbm: number): 'excellent' | 'good' | 'fair' | 'poor' {
  if (dbm >= -55) return 'excellent';
  if (dbm >= -70) return 'good';
  if (dbm >= -85) return 'fair';
  return 'poor';
}

/** A diagnostic is finished when its tone is anything but `pending`. */
export function isSettled(result: DiagnosticResult | null | undefined): boolean {
  return !!result && result.tone !== 'pending';
}

/**
 * @param result        the ack for this step, if one has arrived
 * @param freshReading  whether the node row moved — corroboration, and the only
 *                      evidence available when the ack cannot be parsed
 */
export function stepVerdict(
  result: DiagnosticResult | null | undefined,
  freshReading: boolean,
): StepVerdict {
  if (!result || result.tone === 'pending') return 'pending';
  // 'stale' is the server's judgement that the command has sat past the point
  // where the bridge should have run it — the site is not picking it up.
  if (result.tone === 'stale') return freshReading ? 'answered' : 'no-result';
  if (result.tone === 'error' || result.status === 'ERROR') return 'refused';

  const answered = answeredFromDetail(result.detail);
  if (answered === null) return freshReading ? 'answered' : 'no-result';
  if (answered > 0) return 'answered';
  // The bridge counted zero replies. A node row that moved anyway is more
  // likely to be ordinary traffic than a late answer, so the count wins.
  return 'silent';
}

export interface TestSummary {
  tone: 'success' | 'warn' | 'error' | 'pending';
  headline: string;
  /** One sentence of what to do about it, where there is something to do. */
  advice: string | null;
}

/**
 * The single line at the top of the result panel.
 *
 * `noun` is "node" on XBee sites and "router" on ESP-NOW ones (utils/nodeLabel),
 * lowercase.
 */
export function summariseTest(steps: TestStep[], noun = 'node'): TestSummary {
  const by = (key: TestStep['key']) => steps.find(s => s.key === key)?.verdict ?? 'pending';
  const signal = by('signal');
  const outputs = by('outputs');
  const verdicts = [signal, outputs];

  if (verdicts.includes('refused')) {
    return {
      tone: 'error',
      headline: 'The check could not be run',
      advice: `The bridge did not get as far as asking this ${noun}. The detail below `
        + 'says why — a disconnected coordinator radio is the usual answer.',
    };
  }

  if (verdicts.includes('pending')) {
    return { tone: 'pending', headline: 'Waiting for the site to answer', advice: null };
  }

  if (signal === 'answered' && outputs === 'answered') {
    return { tone: 'success', headline: `This ${noun} answered both checks`, advice: null };
  }

  if (signal === 'silent' && outputs === 'silent') {
    return {
      tone: 'error',
      headline: `No answer from this ${noun}`,
      advice: `The radio asked twice and heard nothing. Check the ${noun} has power, `
        + 'and whether anything has changed between it and the coordinator.',
    };
  }

  if (signal === 'answered' || outputs === 'answered') {
    return {
      tone: 'warn',
      headline: `This ${noun} answered one check of two`,
      advice: signal === 'answered'
        ? 'The radio is reachable but did not report its output pins — which is what '
          + 'IO sampling being off looks like, not necessarily a fault.'
        : 'It reported its outputs but not a signal reading. Worth running again '
          + 'before reading anything into it.',
    };
  }

  return {
    tone: 'warn',
    headline: 'No result came back',
    advice: 'The commands were queued but the site never reported what it did with '
      + 'them. That points at the bridge rather than at this ' + noun + '.',
  };
}
